import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { track } from "../analytics";
import { IconInfo } from "../control/ui/icons";
import { captureFocusReturn } from "../lib/focusReturn";
import {
  GUIDES,
  dismissGuideForSession,
  dismissGuideForever,
  guideDismissed,
  type GuideContent,
  type GuideSurface,
} from "./guides";

/**
 * One surface's guide, as a dialog — the shape the old WallIntroDialog proved
 * (capped scrollable card, scrim dismissal, `first-run | info` analytics split),
 * generalized until it absorbed the wall itself: content comes from guides.tsx,
 * the wall's included.
 *
 * TWO WAYS OUT, AND THEY MEAN DIFFERENT THINGS. "Got it" (and its equivalents, the
 * scrim and Escape) keeps the guide shut for the rest of this tab's SESSION — product's
 * call, "Got it lasts the session" — and the guide RETURNS in a fresh session: the
 * original ask ("shows up every single time … until they click don't show again") still
 * holds across sessions, without re-popping on every navigation and reload of one.
 * "Don't show this again" is the only writer of the forever flag, quiet and underneath,
 * for the reader who actually has it. Both flags are per surface (see guides.tsx).
 *
 * The host decides WHETHER to auto-open (guideDismissed + its own stacking rules — see
 * DisplayApp's take-turns gates); this component only renders and settles the flag.
 */
/**
 * A control page's whole guide wiring in one hook: auto-open on arrival unless the
 * reader said "don't show this again", or "Got it" earlier this session (IN AN EFFECT, not the initializer — the check
 * harness renders these pages with renderToStaticMarkup, where an initializer-opened
 * portal would throw and an effect simply never runs), plus the ⓘ door.
 *
 * The DISPLAY surface does not use this hook — its auto-open stands behind gates a
 * control page does not have (the pairing card, the phone cutoff; see DisplayApp).
 * A control page is a document: a dialog over it is fine at every width.
 */
export function useSurfaceGuide(surface: GuideSurface, autoOpen = true): {
  /** Render the guide when non-null; the value is the analytics source. */
  source: "first-run" | "info" | null;
  openInfo: () => void;
  close: () => void;
} {
  const [source, setSource] = useState<"first-run" | "info" | null>(null);
  useEffect(() => {
    if (autoOpen && !guideDismissed(surface)) setSource("first-run");
    // Arrival only — a dismissal mid-visit must stick for the visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);
  return {
    source,
    openInfo: () => setSource("info"),
    close: () => setSource(null),
  };
}

/** The ⓘ a control page wears beside its heading — the way back to a guide that was
 *  dismissed, and the phone's only door on the display surface. */
export function GuideInfoButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[var(--color-edge)] text-zinc-400 transition-colors hover:border-[var(--color-accent)]/60 hover:text-zinc-200"
    >
      <IconInfo width={13} height={13} />
    </button>
  );
}

/** What Tab can land on inside the card — the same list ClipViewer traps with. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SurfaceGuide({
  surface,
  source,
  content,
  onClose,
}: {
  surface: GuideSurface;
  /** The automatic first-run door, or somebody pressing ⓘ. One measures reach, the
   *  other measures "this page did not explain itself the first time". */
  source: "first-run" | "info";
  /**
   * A live-state variant of the surface's content, still authored in guides.tsx —
   * today that is WALL_GUIDE_FREE, handed in by DisplayApp while a free square
   * actually exists on the board. Absent means GUIDES[surface], which is every
   * static render's truth.
   */
  content?: GuideContent;
  onClose: () => void;
}) {
  const guide = content ?? GUIDES[surface];

  useEffect(() => {
    track("guide_shown", { surface, source });
    // Once per OPEN, deliberately: re-opening from ⓘ is a real event, not a duplicate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (forever: boolean) => {
    if (forever) dismissGuideForever(surface);
    else dismissGuideForSession(surface);
    track("guide_dismissed", { surface, forever });
    onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  /*
   * A MODAL FOR THE KEYBOARD TOO. 2026-10-05 evening QA (keyboard-only, desktop): the
   * "This is Infinite Scroll" card never took focus, ~20 Tabs walked the page behind the
   * scrim before reaching "Got it", and Escape did nothing. Now: focus moves into the
   * card on open and goes back where it was on close; Tab / Shift+Tab cycle the card's
   * own buttons; Escape is "Got it" (this session only — it never writes the forever flag).
   * Keys pressed inside the card stop at the document so the wall's window-level
   * Space-skips / arrow-cycles don't fire behind it (and Space can press "Got it").
   */
  useEffect(() => {
    // The opener, or the ☰ when the opener was a row of the now-closed menu.
    const restoreFocus = captureFocusReturn();
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current(false);
        return;
      }
      if (e.key === "Tab") {
        const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!first || !last) {
          e.preventDefault();
          panel.focus({ preventScroll: true });
        } else if (!active || !panel.contains(active)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && (active === first || active === panel)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (panel.contains(e.target as Node)) e.stopPropagation();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreFocus();
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={() => close(false)}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        /* CAPPED FLEX COLUMN. Header + steps scroll; CTA stack stays painted below
           the fold so short desktop (e.g. 1280×600 Infinite Scroll first-run) never
           clips "Don't show this again". `dvh`, not `vh` — on iOS `vh` measures the
           LARGE viewport, so with the address bar showing the card would be taller
           than the fraction it asked for. Safe-area padding keeps notches/home
           indicators off the quiet secondary CTA. */
        className="flex max-h-[min(90dvh,calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom)))] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] shadow-2xl outline-none"
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-0">
          <h2 id={titleId} className="text-2xl font-bold leading-tight text-zinc-100">{guide.headline}</h2>
          <p className="mb-5 mt-2 text-base leading-snug text-zinc-300">{guide.sub}</p>

          <ul className="mb-5 space-y-2.5">
            {guide.steps.map((s) => (
              <li key={s.title} className="flex gap-2.5">
                {/* The REAL glyph, from the same component the button renders — the
                    reader matches row to control by shape, not by faith. */}
                <span className="mt-0.5 shrink-0 text-[var(--color-accent-soft)]" aria-hidden>
                  {s.icon}
                </span>
                <div>
                  <p className="text-sm font-medium text-zinc-100">{s.title}</p>
                  <p className="text-xs text-zinc-500">{s.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="shrink-0 border-t border-[var(--color-edge)]/60 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
          <button
            type="button"
            onClick={() => close(false)}
            className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-black hover:opacity-90"
          >
            Got it
          </button>
          {/* The one writer of the flag. Quiet on purpose: the accent button is for
              everyone, this line is for the reader who is done being taught. */}
          <button
            type="button"
            onClick={() => close(true)}
            className="mt-2 w-full rounded-lg px-4 py-2 text-center text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Don&rsquo;t show this again
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
